import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService as PrimeNgConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { RootMenuComponent } from './root-menu/root-menu.component';
import { SipalService } from '../components-sipal/sipal.service';
import { AuthenticationService } from '../services/authentication.service';
import { BrandingService } from '../services/branding.service';
import { ConfirmationService } from '../services/confirmation.service';
import { DefaultCustomProjectConfiguration } from '../services/default-custom-project.configuration';
import { SpinnerService } from '../services/spinner.service';
import { SwPushService } from '../services/sw-push';
import { ToastService } from '../services/toast.service';
import { SpinnerComponent } from '../widgets/spinner/spinner.component';

@Component({
  selector: 'app-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss'],
  standalone: true,
  imports: [ConfirmDialogModule, RootMenuComponent, RouterOutlet, ToastModule, TranslateModule, SpinnerComponent],
  providers: [
    ConfirmationService,
    DialogService,
    PrimeNgConfirmationService,
    MessageService,
    ToastService,
    TranslateService,
  ],
})
export class RootComponent implements OnInit {
  @ViewChild(RootMenuComponent, {
    static: true,
  })
    rootMenu!: RootMenuComponent;

  readonly translate = inject(TranslateService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly brandingService = inject(BrandingService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly swPushService = inject(SwPushService);
  // CustomerServices must be injectect at root level to let them register at app bootstrap
  private readonly sipalService = inject(SipalService);
  private readonly defaultCustomProjectConfiguration = inject(DefaultCustomProjectConfiguration);

  readonly loggedIn$ = new BehaviorSubject<boolean>(false);

  constructor() {
    void this.init();
  }

  private async init() {
    await this.authenticationService.init();
    setTimeout(() => {
      this.loggedIn$.next(true);
    }, 300);
  }

  ngOnInit() {
    this.brandingService.applicationIcon$.subscribe((src) => {
      const faviconElement = document.getElementById('appFavicon') as HTMLLinkElement;
      faviconElement.href = src;
    });

    this.brandingService.appTitle$.subscribe((appTitle) => {
      document.title = appTitle;
    });

    // Spinner
    combineLatest([this.brandingService.appTitle$, this.loggedIn$, this.translate.get('ready')])
      .pipe(
        map(([appTitle, loggedIn, ready]) => {
          const loading = !loggedIn || !ready;
          const message = this.translate.instant('i18n.App.loading', { tenant: appTitle });
          return { loading, message };
        }),
      )
      .subscribe(({ loading, message }) => {
        if (loading) {
          this.spinnerService.show(message, true);
        } else {
          this.spinnerService.hide();
          if (
            'Notification' in window
            // && Notification.permission === 'default'
          ) {
            void this.swPushService.subscribeToNotifications();
          }
        }
      });
  }

  toggleMenu(menuItems: MenuItem[], event: Event) {
    this.rootMenu.setMenuItems(menuItems);
    this.rootMenu.toggleMenu(menuItems, event);
  }
}
